import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Award, TrendingUp, TrendingDown, Target,
  Loader2, ChevronRight, Check, BookOpen, Sunrise, Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import AsyncSurface from "../components/AsyncSurface";

/**
 * PdiCards — Phase 11.2 re-land (Feb 6, 2026).
 *
 * Extracted from PDIHub.js so that consumers (InstructorProgress.js)
 * can import these PDI-specific cards without depending on the PDI
 * Home launcher file. PDIHub.js itself is now a 5-button launcher
 * (per owner P0 directive) and intentionally exports nothing else.
 *
 * Identical behaviour to the pre-extraction Phase 11.2 versions.
 * No logic changes.
 */

const API = process.env.REACT_APP_BACKEND_URL;

// ── Supervisor invite + notes ──
//
// Phase 1.3 alive-contract — the previous load() function used
// per-axios `.catch(() => empty)` swallows + an outer silent
// try/catch which made the card silently pretend "0 supervisors
// linked" even when the endpoints were actually unreachable.
// Now the fetch tracks `loading` + `error` explicitly and the
// card body is wrapped in <AsyncSurface> so the user sees a
// calm "Couldn't load your supervisor data" + retry instead.
export function SupervisorCard() {
  const [supervisors, setSupervisors] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [allowCalendar, setAllowCalendar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, n] = await Promise.allSettled([
        axios.get(`${API}/api/pdi/supervisors`, { withCredentials: true }),
        axios.get(`${API}/api/pdi/supervisor-notes`, { withCredentials: true }),
      ]);
      // Both endpoints inform the same card surface — if EITHER
      // failed the user must know rather than see fake "0 linked".
      const failures = [s, n].filter(r => r.status === "rejected");
      if (failures.length === 2) {
        setError("Couldn't load your supervisor data.");
        setSupervisors([]);
        setNotes([]);
      } else {
        setSupervisors(s.status === "fulfilled" ? (s.value.data.items || []) : []);
        setNotes(n.status === "fulfilled" ? (n.value.data.items || []) : []);
        if (failures.length === 1) {
          setError("Some supervisor data couldn't be loaded.");
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/pdi/supervisor/invite`, {
        email: email.trim(),
        permissions: { calendar: allowCalendar },
      }, { withCredentials: true });
      setLastInviteLink(data.invite_link || "");
      toast.success(data.email_sent ? "Invite emailed" : "Invite link ready — share it manually");
      setEmail("");
      setAllowCalendar(false);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const revoke = async (id) => {
    if (!window.confirm("Remove this supervisor?")) return;
    try {
      await axios.delete(`${API}/api/pdi/supervisors/${id}`, { withCredentials: true });
      toast.success("Removed");
      await load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="pdi-supervisor-card" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
      <AsyncSurface
        testid="pdi-supervisor"
        loading={loading}
        error={error}
        empty={false}
        loadingLabel="Loading your supervisor data…"
        onRetry={load}
      >
      <button onClick={() => setShowInvite(!showInvite)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
          <Users className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">Supervisor (optional)</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {supervisors.length > 0
              ? `${supervisors.length} supervisor${supervisors.length !== 1 ? "s" : ""} linked`
              : "Invite your ADI — they don't need their own account here"}
          </p>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${showInvite ? "rotate-90" : ""}`} />
      </button>

      {showInvite && (
        <div className="p-4 border-t border-slate-100 dark:border-slate-700 space-y-3">
          {supervisors.length > 0 && (
            <div className="space-y-1.5">
              {supervisors.map(s => (
                <div key={s.id} data-testid={`supervisor-${s.id}`} className="flex items-center gap-2 text-xs bg-slate-50 dark:bg-slate-700/40 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 dark:text-white truncate">{s.supervisor_name || s.supervisor_email}</p>
                    <p className="text-[10px] text-slate-400">
                      {s.status === "accepted" ? "✓ Active" : "⏳ Pending accept"}
                      {s.permissions?.calendar ? " · Calendar visible" : ""}
                    </p>
                  </div>
                  <button data-testid={`revoke-${s.id}`} onClick={() => revoke(s.id)}
                    className="text-[10px] font-bold text-red-500 hover:text-red-700">Remove</button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <input data-testid="sup-invite-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="supervisor@example.com"
              className="w-full h-10 border border-slate-200 dark:border-slate-600 rounded-xl px-3 text-sm bg-white dark:bg-slate-700" />
            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
              <input data-testid="sup-invite-calendar" type="checkbox" checked={allowCalendar} onChange={e => setAllowCalendar(e.target.checked)} />
              <span>Also let them see my calendar (optional)</span>
            </label>
            <Button data-testid="sup-invite-send" onClick={sendInvite} disabled={saving}
              className="w-full h-10 bg-indigo-600 text-white rounded-xl text-sm font-bold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Invite"}
            </Button>
            {lastInviteLink && (
              <div className="bg-indigo-50 dark:bg-indigo-900/15 rounded-lg p-2">
                <p className="text-[10px] font-bold text-indigo-600 uppercase mb-1">Share this link directly if needed</p>
                <p className="text-[10px] text-slate-600 break-all">{lastInviteLink}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div data-testid="supervisor-notes" className="p-4 border-t border-slate-100 dark:border-slate-700 space-y-2">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Notes From Your Supervisor</p>
          {notes.slice(0,5).map(n => (
            <div key={n.id} className="bg-slate-50 dark:bg-slate-700/40 rounded-lg p-2.5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 uppercase">{n.category.replace("_", " ")}</span>
                <span className="text-[10px] text-slate-400 ml-auto">{new Date(n.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{n.text}</p>
              <p className="text-[10px] text-slate-400 mt-1">— {n.supervisor_name}</p>
            </div>
          ))}
        </div>
      )}
      </AsyncSurface>
    </div>
  );
}

// ── Readiness Score Card ──
export function ReadinessCard({ readiness, passInfo }) {
  if (!readiness) return null;
  const Icon = passInfo?.icon || BookOpen;
  const trend = readiness.trend;
  return (
    <div data-testid="pdi-readiness-card" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-violet-500" />
          <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Part 3 Readiness</p>
        </div>
        {passInfo && (
          <span data-testid="pdi-likely-pass" className={`text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${passInfo.color}`}>
            <Icon className="w-3 h-3" />{passInfo.label}
          </span>
        )}
      </div>

      <div className="flex items-end gap-3">
        <div>
          <p data-testid="pdi-readiness-score" className="text-5xl font-extrabold text-slate-900 dark:text-white tabular-nums">
            {readiness.readiness_score}
            <span className="text-xl text-slate-400">/100</span>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {readiness.competencies_tested}/{readiness.competencies_total} competencies scored · {readiness.lessons_with_data} debriefed lessons
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1 text-xs font-bold">
          {trend === "improving" && <><TrendingUp className="w-4 h-4 text-emerald-500" /><span className="text-emerald-600">Improving</span></>}
          {trend === "declining" && <><TrendingDown className="w-4 h-4 text-red-500" /><span className="text-red-600">Declining</span></>}
          {trend === "steady" && <span className="text-slate-400">Steady</span>}
        </div>
      </div>

      {readiness.strengths.length > 0 && (
        <div className="mt-3 flex items-start gap-2 text-xs">
          <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5" />
          <p className="text-slate-600 dark:text-slate-400">
            <span className="font-bold text-emerald-600">{readiness.strengths.length} strength{readiness.strengths.length !== 1 ? "s" : ""}:</span>{" "}
            built up {readiness.strengths.length} competenc{readiness.strengths.length !== 1 ? "ies" : "y"}
          </p>
        </div>
      )}
    </div>
  );
}

// ── 17 Competencies Breakdown ──
export function CompetencyBreakdown({ readiness }) {
  const [expanded, setExpanded] = useState(false);
  if (!readiness?.per_competency) return null;

  const byCategory = {};
  readiness.per_competency.forEach(c => {
    (byCategory[c.category] ??= []).push(c);
  });

  const statusColor = (status) => ({
    strong: "bg-emerald-500",
    developing: "bg-amber-400",
    weak: "bg-red-400",
    untested: "bg-slate-200 dark:bg-slate-700",
  }[status]);

  return (
    <div data-testid="pdi-competency-breakdown" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-indigo-500" />
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">17 Competencies</p>
        </div>
        <button data-testid="expand-competencies" onClick={() => setExpanded(e => !e)}
          className="text-[11px] font-bold text-indigo-600">
          {expanded ? "Collapse" : "Expand all"}
        </button>
      </div>

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="mt-3">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{cat}</p>
          <div className="space-y-1.5">
            {items.map(c => (
              <div key={c.id} className="flex items-start gap-2">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${statusColor(c.status)}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs leading-tight ${expanded ? "" : "line-clamp-1"}
                    ${c.status === "weak" ? "text-red-600 dark:text-red-400 font-bold"
                    : c.status === "strong" ? "text-emerald-600 dark:text-emerald-400 font-medium"
                    : c.status === "developing" ? "text-amber-700 dark:text-amber-400"
                    : "text-slate-400"}`}>
                    {c.label}
                  </p>
                  {expanded && c.score != null && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      Score {c.score} · ✓ {c.demonstrated} · ✗ {c.missed}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center gap-3 text-[10px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-slate-500">Strong</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /><span className="text-slate-500">Developing</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /><span className="text-slate-500">Weak</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" /><span className="text-slate-500">Untested</span></span>
      </div>
    </div>
  );
}

// ── End of Day Reflection ──
export function EndOfDayCard({ todayEod, onDone, open, setOpen }) {
  const [wentWell, setWentWell] = useState("");
  const [needsImproving, setNeedsImproving] = useState("");
  const [tomorrowFocus, setTomorrowFocus] = useState("");
  const [howIFelt, setHowIFelt] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!wentWell && !needsImproving && !tomorrowFocus && !howIFelt) {
      toast.error("Write something in at least one field"); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/pdi/end-of-day`, {
        went_well: wentWell, needs_improving: needsImproving,
        tomorrow_focus: tomorrowFocus, how_i_felt: howIFelt,
      }, { withCredentials: true });
      toast.success("End-of-day reflection saved");
      setWentWell(""); setNeedsImproving(""); setTomorrowFocus(""); setHowIFelt("");
      setOpen(false);
      await onDone?.();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (todayEod) {
    return (
      <div data-testid="eod-saved" className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/15 dark:to-purple-900/10 border border-violet-200 dark:border-violet-800 rounded-2xl p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Sunrise className="w-4 h-4 text-violet-500" />
          <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Today's Reflection</p>
          <span className="ml-auto text-[10px] text-slate-400">{todayEod.lessons_today} lesson{todayEod.lessons_today !== 1 ? "s" : ""}</span>
        </div>
        {todayEod.ai_summary && (
          <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed italic">"{todayEod.ai_summary}"</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="pdi-eod-card" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40">
        <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
          <Sunrise className="w-5 h-5 text-violet-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">End of Day Reflection</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {open ? "Write honestly — AI will summarise" : "After your last lesson, reflect on today"}
          </p>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 dark:border-slate-700 pt-3">
          <Field label="What went well today?" testId="eod-went-well" value={wentWell} onChange={setWentWell}
            placeholder="e.g. explained roundabout rules clearly, stayed calm during a tricky moment..." />
          <Field label="What needs improving?" testId="eod-needs-improving" value={needsImproving} onChange={setNeedsImproving}
            placeholder="e.g. didn't give enough wait time after questions, rushed the recap..." />
          <Field label="Tomorrow's focus?" testId="eod-tomorrow-focus" value={tomorrowFocus} onChange={setTomorrowFocus}
            placeholder="e.g. practise Socratic questions, slow down feedback sequence..." />
          <Field label="How did you feel?" testId="eod-how-i-felt" value={howIFelt} onChange={setHowIFelt}
            placeholder="e.g. stretched but energised, a bit deflated after lesson 3..." />
          <Button data-testid="eod-submit" onClick={submit} disabled={saving}
            className="w-full h-10 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-bold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Reflection"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block">{label}</label>
      <textarea data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-16 border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2 text-xs bg-white dark:bg-slate-700 resize-none focus:ring-2 focus:ring-violet-500" />
    </div>
  );
}
